import api from '../services/api'
// Type for registration fields
export type RegistrationField = {
  id: string
  name: string
  type: string
  required: boolean
  options?: { value: string; label: string }[]
}
// Preload registration fields
const preloadedFields = [
  {
    id: 'empresaTipo',
    name: 'Tipo de empresa',
    type: 'select',
    required: true,
    options: [
      { value: 'agencia', label: 'Agência' },
      { value: 'ecommerce', label: 'E-commerce' },
      { value: 'empresa-privada', label: 'Empresa privada' },
      { value: 'empresa-publica', label: 'Empresa pública' },
      { value: 'imprensa', label: 'Imprensa' },
      { value: 'outros', label: 'Outros' },
    ],
  },
  {
    id: 'cargo',
    name: 'Cargo',
    type: 'select',
    required: true,
    options: [
      { value: 'ceo', label: 'CEO' },
      { value: 'fundador', label: 'Fundador' },
      { value: 'presidente', label: 'Presidente' },
      { value: 'diretor', label: 'Diretor' },
      { value: 'gerente', label: 'Gerente' },
      { value: 'coordenador', label: 'Coordenador' },
      { value: 'analista', label: 'Analista' },
      { value: 'consultor', label: 'Consultor' },
      { value: 'autonomo', label: 'Autônomo' },
      { value: 'supervisor', label: 'Supervisor' },
      { value: 'assessor', label: 'Assessor' },
      { value: 'assistente', label: 'Assistente' },
      { value: 'estagiario', label: 'Estagiário' },
      { value: 'estudante', label: 'Estudante' },
      { value: 'professor', label: 'Professor' },
    ],
  },
  {
    id: 'funcionariosQuantidade',
    name: 'Quantidade de funcionários',
    type: 'select',
    required: true,
    options: [
      { value: '1 - 10', label: '1 - 10' },
      { value: '11 - 20', label: '11 - 20' },
      { value: '21 - 50', label: '21 - 50' },
      { value: '51 - 100', label: '51 - 100' },
      { value: '101 - 200', label: '101 - 200' },
      { value: '201 - 500', label: '201 - 500' },
      { value: '501 - 1.000', label: '501 - 1.000' },
      { value: '1.001 - 5.000', label: '1.001 - 5.000' },
      { value: 'Acima de 5.000', label: 'Acima de 5.000' },
    ],
  },
  {
    id: 'pretendeContratarSeo',
    name: 'Pretende contratar serviços de SEO?',
    type: 'select',
    required: true,
    options: [
      { value: 'sim-3-meses', label: 'Sim, nos próximos 3 meses' },
      { value: 'sim-3-6-meses', label: 'Sim, nos próximos 3-6 meses' },
      { value: 'sim-sem-data', label: 'Sim, sem data definida' },
      { value: 'nao-nao-prioridade', label: 'Não, não é prioridade' },
      { value: 'nao-time-interno', label: 'Não, temos time interno' },
      { value: 'nao-agencia-atual', label: 'Não, temos agência atual' },
    ],
  },
]
// Function to fetch registration fields from API
export const fetchRegistrationFields = async (): Promise<
  RegistrationField[]
> => {
  // Check if we have cached fields
  const cachedData = localStorage.getItem('registrationFields')
  if (cachedData) {
    const { fields, timestamp } = JSON.parse(cachedData)
    // Check if cache is still valid (less than 24 hours old)
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000
    if (Date.now() - timestamp < twentyFourHoursInMs) {
      return fields
    }
  }
  // Return preloaded fields immediately to avoid loading delay
  // And fetch from API in the background to update cache
  setTimeout(async () => {
    try {
      const response = await api.get('/api/registration/fields')
      const fields = response.data
      // Cache the fields with timestamp
      localStorage.setItem(
        'registrationFields',
        JSON.stringify({
          fields,
          timestamp: Date.now(),
        }),
      )
    } catch (error) {
      console.error('Failed to fetch registration fields in background:', error)
    }
  }, 100)
  // Cache the preloaded fields if no cache exists
  if (!cachedData) {
    localStorage.setItem(
      'registrationFields',
      JSON.stringify({
        fields: preloadedFields,
        timestamp: Date.now(),
      }),
    )
  }
  // Return preloaded fields or cached fields
  return cachedData ? JSON.parse(cachedData).fields : preloadedFields
}
// Function to format phone number to Brazilian format
export const formatBrazilianPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '')
  if (digits.length <= 2) {
    return digits
  } else if (digits.length <= 7) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  } else {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`
  }
}
// Function to parse formatted phone number back to raw digits
export const parsePhoneNumber = (formattedNumber: string): string => {
  return formattedNumber.replace(/\D/g, '')
}
